
import { useToast } from "@/hooks/use-toast";
import { getSupabaseClient } from "@/hooks/useOrdersManagement/supabaseClients";
import { useQueryClient } from "@tanstack/react-query";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
  square_item_id?: string;
  square_variation_id?: string;
}

import { Order } from "./useOrdersManagement/types";

export const useProductOperations = (
  order: Order,
  products: Product[]
) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const checkProductInCatalog = async (barcode: string) => {
    const client = getSupabaseClient();
    const { data: catalogData } = await client
      .from('product_catalog')
      .select('name, square_item_id, square_variation_id')
      .eq('barcode', barcode)
      .maybeSingle();

    return catalogData;
  };

  const handleAddProduct = async (newProduct: {
    barcode: string;
    name: string;
    boxes: number;
    unitsPerBox: number;
    photos: string[];
    creationSource?: 'manual' | 'scan' | 'auto_match' | 'merge_result' | 'import';
    bypassDuplicateCheck?: boolean;
  }) => {
    if (!newProduct.barcode) {
      toast({
        title: "Error",
        description: "Please scan or enter a barcode.",
        variant: "destructive"
      });
      return false;
    }

    // Require at least one product photo for ALL creation flows
    if (!newProduct.photos || newProduct.photos.length === 0) {
      toast({
        title: "Photo Required",
        description: "Please add at least one product photo to continue.",
        variant: "destructive"
      });
      return false;
    }

    const creationSource = newProduct.creationSource || 'manual';

    // Guard: scanned products must include at least one photo
    if (creationSource === 'scan' && (!newProduct.photos || newProduct.photos.length === 0)) {
      toast({
        title: "Photo Required",
        description: "Fast Track scan requires a product photo. Please capture a photo and try again.",
        variant: "destructive"
      });
      return false;
    }

    // Check for duplicates unless bypassed
    if (!newProduct.bypassDuplicateCheck) {
      const client = getSupabaseClient();
      const { data: duplicates } = await client
        .rpc('detect_product_duplicates', {
          p_barcode: newProduct.barcode,
          p_name: newProduct.name || `Product ${newProduct.barcode}`,
          p_order_id: order.id
        });

      if (duplicates && duplicates.length > 0) {
        const exactDuplicate = duplicates.find((d: any) => d.similarity_score === 1.0);
        
        // Prevent automated duplicates
        if (creationSource === 'auto_match' || creationSource === 'merge_result') {
          console.warn('Automated duplicate prevention:', {
            barcode: newProduct.barcode,
            name: newProduct.name,
            existingProducts: duplicates,
            creationSource
          });
          
          toast({
            title: "Duplicate Prevented",
            description: `Product "${newProduct.name}" already exists. Automated duplicate creation prevented.`,
            variant: "destructive"
          });
          return false;
        }

        // Warn about manual duplicates
        if (exactDuplicate && (creationSource === 'manual' || creationSource === 'scan')) {
          const shouldContinue = window.confirm(
            `A product with the same barcode and name already exists (created via ${exactDuplicate.creation_source}). Do you want to create a duplicate anyway?`
          );
          
          if (!shouldContinue) {
            return false;
          }
        }
      }
    }

    // Check if product exists in catalog to get Square IDs
    const catalogProduct = await checkProductInCatalog(newProduct.barcode);

    // Normalize photos to ensure proper format
    const normalizedPhotos = newProduct.photos && Array.isArray(newProduct.photos) 
      ? newProduct.photos.filter(photo => photo && typeof photo === 'string' && photo.trim().length > 0)
      : [];

    console.log('📸 Normalizing photos for database:', {
      originalPhotos: newProduct.photos,
      normalizedPhotos,
      photoCount: normalizedPhotos.length
    });

    const product = {
      id: `PROD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      order_id: order.id,
      barcode: newProduct.barcode,
      name: newProduct.name || `Product ${newProduct.barcode}`,
      boxes: newProduct.boxes,
      units_per_box: newProduct.unitsPerBox,
      photos: normalizedPhotos,
      has_credit: false,
      square_item_id: catalogProduct?.square_item_id || null,
      square_variation_id: catalogProduct?.square_variation_id || null,
      creation_source: creationSource,
      created_by_process: `add_product_${creationSource}`,
      creation_metadata: {
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        catalogFound: !!catalogProduct,
        photoCount: normalizedPhotos.length
      }
    };

    console.log('Creating product with photos and duplicate prevention:', {
      productId: product.id,
      barcode: product.barcode,
      name: product.name,
      photoCount: product.photos.length,
      photos: product.photos,
      creationSource,
      bypassCheck: newProduct.bypassDuplicateCheck
    });

    const client = getSupabaseClient();
    const { data, error } = await client
      .from('products')
      .insert([product])
      .select();

    if (error) {
      console.error('❌ Product creation error:', error);
      toast({
        title: "Error",
        description: "Failed to add product. Please try again.",
        variant: "destructive"
      });
      return false;
    }

    // Update order product count
    const { error: updateError } = await client
      .from('orders')
      .update({ product_count: products.length + 1 })
      .eq('id', order.id);

    if (updateError) {
      console.error('Error updating order product count:', updateError);
    }

    console.log('✅ Product added successfully with database confirmation:', {
      insertedProduct: data?.[0],
      photoCount: data?.[0]?.photos?.length || 0,
      photos: data?.[0]?.photos
    });

    queryClient.invalidateQueries({ queryKey: ['products', order.id] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    
    toast({
      title: "Product Added",
      description: `${product.name} has been added to the order${product.photos.length > 0 ? ' with photo' : ''}.${catalogProduct ? ' (Found in Square catalog)' : ' (New product)'}`,
    });

    return true;
  };

  const handleDeleteProduct = async (product: Product) => {
    if (!confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const client = getSupabaseClient();
      const { error } = await client
        .from('products')
        .delete()
        .eq('id', product.id);

      if (error) throw error;

      // Update order product count
      const { error: updateError } = await client
        .from('orders')
        .update({ product_count: products.length - 1 })
        .eq('id', order.id);

      if (updateError) {
        console.error('Error updating order product count:', updateError);
      }

      queryClient.invalidateQueries({ queryKey: ['products', order.id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      
      toast({
        title: "Product Deleted",
        description: `${product.name} has been deleted successfully.`,
      });
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "Failed to delete product. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleScanResult = async (scannedBarcode: string) => {
    console.log(`🔍 Processing scanned barcode: ${scannedBarcode}`);
    
    // Enhanced duplicate prevention: Check for existing products with same barcode
    const existingProducts = products.filter(product => product.barcode === scannedBarcode);
    
    if (existingProducts.length > 0) {
      console.log(`⚠️ Found ${existingProducts.length} existing products with barcode ${scannedBarcode}`);
      console.log('Existing products:', existingProducts.map(p => ({ id: p.id, name: p.name, barcode: p.barcode })));
      
      // Don't create new product, just show info about existing ones
      toast({
        title: "Duplicate Barcode Detected",
        description: `Product with barcode ${scannedBarcode} already exists: "${existingProducts[0].name}". Use the existing product instead of creating a duplicate.`,
        variant: "destructive",
        duration: 5000
      });
      
      // Return null to indicate no new product should be created
      return null;
    }
    
    // Only proceed if no existing products found
    const catalogProduct = await checkProductInCatalog(scannedBarcode);
    
    const productData = { 
      barcode: scannedBarcode,
      name: catalogProduct?.name || '',
      boxes: 1,
      unitsPerBox: 1,
      photos: [] as string[],
      creationSource: 'scan' as const
    };
    
    console.log('Scan result processed (no duplicates found):', {
      barcode: scannedBarcode,
      foundInCatalog: !!catalogProduct,
      creationSource: 'scan',
      existingProductsCount: existingProducts.length,
      timestamp: new Date().toISOString()
    });
    
    toast({
      title: catalogProduct ? "Product Found in Square" : "New Product",
      description: catalogProduct 
        ? `${catalogProduct.name} found in Square catalog` 
        : `Barcode ${scannedBarcode} not found in Square - will be added as new product`,
    });
    
    return productData;
  };

  return {
    checkProductInCatalog,
    handleAddProduct,
    handleDeleteProduct,
    handleScanResult
  };
};
